import { z } from 'zod';

/**
 * Configuration the container knows and the bundle cannot.
 *
 * `VITE_*` is inlined by Vite at build time, which is right for `VITE_API_URL`
 * (a bundle that talks to nobody should not build) and wrong for anything an
 * operator sets per deployment after the image exists. `VITE_SENTRY_DSN` was
 * an ARG with an empty default, nobody passed it in Dokploy, and the panel
 * built green and told Sentry nothing for months.
 *
 * So `index.html` loads `/config.js` before the bundle. In the repo it is the
 * empty file in `public/`; in the container the nginx entrypoint rewrites it
 * from the runtime env (`dokploy/write-runtime-config.sh`). The bundle stays
 * identical across environments and the values travel with the container.
 *
 * Anything `VITE_*` still works as a fallback: a runtime value that is absent
 * or unusable falls through to the build-time one, never the other way round.
 */

declare global {
  interface Window {
    __KO_CONFIG__?: unknown;
  }
}

export interface RuntimeConfig {
  sentryDsn?: string;
  environment?: string;
}

/**
 * Read once per call, not once per module: `initObservability` runs before the
 * first render, and reading lazily keeps the tests honest about what the
 * global held at that moment.
 *
 * Nothing here throws. The file is edited by an operator in Dokploy, so a bad
 * value must cost that one value — with a console line saying so — and never
 * the mount. Each field is judged on its own: a misspelt `environment` does
 * not take the DSN down with it.
 */
export function readRuntimeConfig(
  win: Pick<Window, '__KO_CONFIG__'> | undefined = globalWindow(),
): RuntimeConfig {
  const raw = win?.__KO_CONFIG__;
  if (raw === undefined || raw === null) return {};
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    console.error('[config] /config.js did not set an object; ignoring it');
    return {};
  }
  const obj = raw as Record<string, unknown>;
  return {
    sentryDsn: field(obj, 'sentryDsn', z.string().url()),
    environment: field(obj, 'environment', z.string()),
  };
}

/**
 * A blank string is "not set": the entrypoint renders `${SENTRY_DSN:-}` into
 * the file, so an unset variable arrives as `""`, not as a missing key.
 */
function field<T>(obj: Record<string, unknown>, key: string, schema: z.ZodType<T>): T | undefined {
  const value = obj[key];
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string' && value.trim() === '') return undefined;
  const parsed = schema.safeParse(typeof value === 'string' ? value.trim() : value);
  if (!parsed.success) {
    console.error(`[config] /config.js: ignoring ${key}:`, parsed.error.issues[0]?.message);
    return undefined;
  }
  return parsed.data;
}

function globalWindow(): Window | undefined {
  return typeof window === 'undefined' ? undefined : window;
}
